/**
 * Readable names for drifted settings and DSC resources.
 *
 * Drift rows carry configuration keys: M365DSC components
 * ("AADNamedLocationPolicy") and property names ("IsTrusted",
 * "AutoForwardEnabled"). No curated display-name source exists for them (TCM
 * resource types have one, `tcmResourceNames.generated.ts`, used as-is), so
 * these helpers split the key mechanically into sentence-case words
 * ("Auto-forward enabled"). That is honest: the words are the key's own, never
 * an invented business name. The raw key stays the source of truth on the row
 * and is shown where an admin needs it (the expanded drift detail).
 *
 * Pure functions only; unit-tested in drift-names.test.ts.
 */

import { prettifyResourceType, workloadLabelForResourceType } from './tcmResourceNames'

/** Product names a camelCase split would break in two ("Share Point"). */
const COMPOUNDS: ReadonlyArray<readonly [string, string]> = [
  ['Share', 'Point'],
  ['One', 'Drive'],
  ['One', 'Note'],
  ['Power', 'Shell'],
  ['Linked', 'In'],
  ['You', 'Tube'],
]

/** Proper nouns that keep their capital mid-sentence. */
const PROPER = new Set([
  'Microsoft', 'SharePoint', 'OneDrive', 'OneNote', 'PowerShell', 'Teams', 'Exchange', 'Outlook', 'Windows',
  'Office', 'Azure', 'Entra', 'Intune', 'Defender', 'Purview', 'Yammer', 'Viva', 'Skype', 'Android', 'LinkedIn',
  'YouTube',
])

/**
 * Mixed-case terms the camelCase split breaks ("OAuth2" → "O auth 2", "IPv6" →
 * "I pv 6", "iOS" → "I OS"). Matched whole where a token starts, ahead of the
 * generic split, and kept as written, even first in a sentence. Longest first,
 * so "OAuth2" wins over "OAuth".
 */
const TERMS = [
  'OAuth2', 'OAuth', 'IPv4', 'IPv6', 'IPsec', 'iPadOS', 'iPad', 'iPhone', 'iOS', 'macOS', 'tvOS', 'watchOS',
  'eTag', 'ETag', 'eDiscovery', 'WiFi',
]
const TERM_SET = new Set(TERMS)

/** A term, a plural acronym ("IDs", "URLs"), an acronym, a word, or a number. */
const TOKEN = new RegExp(`(?:${TERMS.join('|')})(?![a-z])|[A-Z]{2,}s(?![a-z])|[A-Z]+(?![a-z])|[A-Z]?[a-z]+|\\d+`, 'g')

/** Terms, acronyms ("MFA", "AAD", "IDs") and numbers keep their form. */
function isVerbatim(token: string): boolean {
  return (
    TERM_SET.has(token) ||
    /^\d+$/.test(token) ||
    /^[A-Z]{2,}s$/.test(token) ||
    (token.length >= 2 && token === token.toUpperCase())
  )
}

/**
 * "AutoForwardEnabled" → "Auto-forward enabled", "RequireMFAForAdmins" →
 * "Require MFA for admins". Terms, acronyms, numbers and product names keep
 * their form; an "Auto" prefix joins its word with a hyphen. Text that already
 * has a space is prose and comes back unchanged, and so does a key the split
 * would leave with a one-letter word ("X509Thumbprint", "PolicyA"): the raw
 * key beats a mangled name. `midSentence` lowercases the first word too
 * (unless it is an acronym or a product name).
 */
export function humanizeKey(key: string, { midSentence = false }: { midSentence?: boolean } = {}): string {
  const raw = key.trim()
  if (!raw || /\s/.test(raw)) return key
  const tokens = raw.match(TOKEN)
  if (!tokens || tokens.some((t) => /^[A-Za-z]$/.test(t))) return key

  const merged: string[] = []
  for (let i = 0; i < tokens.length; i++) {
    const pair = COMPOUNDS.find(([a, b]) => tokens[i] === a && tokens[i + 1] === b)
    if (pair) {
      merged.push(pair[0] + pair[1])
      i++
    } else {
      merged.push(tokens[i])
    }
  }

  const words: string[] = []
  for (let i = 0; i < merged.length; i++) {
    const token = merged[i]
    const cased = isVerbatim(token) || PROPER.has(token) ? token : token.toLowerCase()
    if (cased === 'auto' && merged[i + 1] && !isVerbatim(merged[i + 1])) {
      words.push(`auto-${merged[i + 1].toLowerCase()}`)
      i++
    } else {
      words.push(cased)
    }
  }
  if (!midSentence && words.length && !TERM_SET.has(words[0])) {
    words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1)
  }
  return words.join(' ')
}

/** A drifted setting's name: the last segment of its property path, humanized. */
export function settingLabel(setting: string): string {
  const leaf = setting.trim().split(/[./]/).filter(Boolean).pop() ?? setting
  return humanizeKey(leaf)
}

// The M365DSC workload prefixes, mirroring `DSC_PREFIX_LABELS` in
// tcmResourceNames.ts. Stripped because the row's workload label already
// names the product.
const DSC_PREFIX = /^(SPO|OneDrive|OD|AAD|EntraID|Identity|EXO|Exchange|Teams|Intune|Defender|MDO|SC|Purview)(?=[A-Z])/

/** A DSC component without its workload prefix: "AADNamedLocationPolicy" → "Named location policy". */
export function dscResourceLabel(component: string, opts: { midSentence?: boolean } = {}): string {
  const rest = component.trim().replace(DSC_PREFIX, '')
  return humanizeKey(rest || component, opts)
}

/**
 * A drift's resource, named for prose: a TCM type through the curated map
 * ("Entra Conditional Access Policy"), a DSC component as workload plus
 * resource ("SharePoint tenant settings"). Anything else comes back as given.
 */
export function driftResourceName(component: string): string {
  const pretty = prettifyResourceType(component)
  if (pretty !== component || /\s/.test(component)) return pretty
  const workload = workloadLabelForResourceType(component)
  if (workload === 'Microsoft 365') return humanizeKey(component)
  return `${workload} ${dscResourceLabel(component, { midSentence: true })}`
}
